import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OperatorFunction, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { BasicService } from './basic.service';

/**
 * A few helper methods for displaying error messages when a service catches an http error.
 *
 * As some service link several http calls with mergeMap,
 * and as 403 errors are handled by the RedirectToLoginInterceptor,
 * the helper methods below set a flag on the HttpErrorResponse passed to them,
 * and will refuse to display the alert if the flag is already set
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlingService {
  /*
   * /!\ /!\ /!\
   * It is not possible to inject AJS providers in this Service due to the mobile application that does not know the AJS part.
   * /!\ /!\ /!\
   */
  constructor(private basicService: BasicService) {}

  /**
   * If the given error has not been handled, displays an error toast with a generic message
   * and mark it as handled;
   * does nothing otherwise
   */
  toastAlertUnknownError(response: HttpErrorResponse) {
    const messageKey = response.status === 0 ? 'm/error/unknown-network-error' : 'm/error/unknown-error';
    this.toastAlertError(response, messageKey);
  }

  /**
   * If the given error has not been handled, displays an error toast with the given message
   * and mark it as handled;
   * does nothing otherwise
   */
  toastAlertError(response: HttpErrorResponse, messageKey: string, messageParams?) {
    // Do not use this mechanism for the widget
    if (this.notYetHandled(response)) {
      console.error('http error: ', response);
      this.markAlreadyHandled(response);
      this.basicService.toastAlert(messageKey, messageParams);
    }
  }

  /**
   * If the given error has not been handled, display an error dialog with the given message,
   * and mark the error as already handled;
   * do nothing otherwise.
   *
   * The returned promise will be resolved when the dialog is dismissed in the fist case,
   * or immediately in the second case
   */
  async modalAlertError(response: HttpErrorResponse, messageKey: string, messageParams?) {
    if (this.notYetHandled(response)) {
      console.error('http error: ', response);
      this.markAlreadyHandled(response);
      return this.basicService.modalAlertWithOK('m/error', messageKey, messageParams);
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Displays a modal alert with an OK button if the given error's reason is found in the given "reason -> message key" dictionnary,
   * or a generic toast alert otherwise.
   *
   * The returned promise will be resolved when the dialog is dismissed in the fist case,
   * or immediately in the second case
   */
  async alertHttpError(response: HttpErrorResponse, knownErrorReasons: { [reason: string]: string }) {
    if (response.error && response.error.error && response.error.error.reason in knownErrorReasons) {
      const reason = response.error.error.reason;
      return this.modalAlertError(response, knownErrorReasons[reason], response.error.error.data);
    } else {
      this.toastAlertUnknownError(response);
      return Promise.resolve();
    }
  }

  /**
   * Convenience method that wraps the error alerting in an rxjs catchError operator which rethrows the error
   */
  catchAlertAndThrowHttpError<T>(): OperatorFunction<T, T> {
    return catchError((response: HttpErrorResponse) => {
      const body = response.error;
      if (body?.error?.error_message_translation_key) {
        this.toastAlertError(
          response,
          body.error.error_message_translation_key,
          body.error.error_message_place_holders
        );
      } else if (body?.error?.reason) {
        this.toastAlertError(response, body.error.reason);
      } else {
        this.toastAlertUnknownError(response);
      }
      return throwError(response);
    });
  }

  private markAlreadyHandled(response: HttpErrorResponse) {
    (response as any).alcAlcreadyHandled = true;
  }

  private notYetHandled(response: HttpErrorResponse) {
    return !(response as any).alcAlcreadyHandled;
  }
}
